import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import PageInstructions from '../../shared/components/PageInstructions';
import GET_LICENSE_INFORMATION_FOR_ORGANIZATION from '../queries/getLicenseInformationForOrganization';
import MuiInputNumber from '../../shared/components/MuiInputNumber';
import { LicenseInformationMapper } from '../mappers/licenseInformationMapper';
import { Page } from '../../layout';
import ApolloError from '../../shared/components/ApolloError';
import ActionButtonGroup from '../../shared/components/ActionButtonsGroup';
import _ from 'lodash';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import UPDATE_LICENSE_INFORMATION from '../queries/updateLicenseInformation';
import LicenseInformationInputValidator from '../validators/licenseInformationInputValidator';
import ErrorsContainer from './ErrorsContainer';
import LICENSE_INFORMATION_HOLDER_TYPES from '../constants/licenseInformationHolderTypes';
import StyledTable from '../../shared/components/StyledTable';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import { getPageTitle } from '../../shared/services/pageTitleService';
import { PAGE_MODE } from '../../userManagement/constants/pageModeTypes';

const LicenseManagementPage = () => {
  const location = useLocation();
  const { activeTab, orgId, orgName, pageMode } = location.state ? location.state : {};

  const [tableData, setTableData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  const pageTitle = getPageTitle([orgName, 'License Management']);
  const annotationItems = [orgName];
  const previousLink = `/user-management/${orgId}`;
  const breadcrumbPathItems = [
    {
      name: 'User Management',
      url: previousLink
    },
    { name: 'License Management', url: '' }
  ];

  const navigate = useNavigate();

  const {
    data: queriedData,
    error,
    loading
  } = useQuery(GET_LICENSE_INFORMATION_FOR_ORGANIZATION, {
    variables: { organizationId: orgId }
  });

  const [updatedLicenseInformations, { error: submitError, loading: isSubmitting }] = useMutation(
    UPDATE_LICENSE_INFORMATION,
    {
      update: cache => {
        cache.evict({});
      }
    }
  );

  const queriedDataMapped = useMemo(() => {
    if (!queriedData) {
      return [];
    }

    const tableData = [
      LicenseInformationMapper.toTableData(
        queriedData.licenseInformationForOrganization.Organization,
        LICENSE_INFORMATION_HOLDER_TYPES.Organization
      ),
      ...queriedData.licenseInformationForOrganization.OrgUnits.map(ouli =>
        LicenseInformationMapper.toTableData(ouli, LICENSE_INFORMATION_HOLDER_TYPES.OrgUnit)
      )
    ];
    setTableData(tableData);

    return [...tableData];
  }, [queriedData]);

  const handleChange = (holderType, holderId, updateValueFunc) => {
    const updatedTableData = getUpdatedState(tableData, holderType, holderId, updateValueFunc);
    const submitDisabled = getChangedObjects(updatedTableData, queriedDataMapped).isEmpty();

    setIsSubmitDisabled(submitDisabled);
    setTableData(updatedTableData);
  };

  const handleCancelClick = () => {
    navigate(-1);
  };

  const handleSubmitClick = async () => {
    const updatedObjects = getChangedObjects(tableData, queriedDataMapped).value();

    const validationErrors =
      await LicenseInformationInputValidator.validateMultiple(updatedObjects);
    setValidationErrors(validationErrors);

    if (validationErrors.length) {
      setIsSubmitDisabled(true);
      return;
    }

    const input = LicenseInformationMapper.toApolloInput(updatedObjects);
    await updatedLicenseInformations({ variables: { data: input } });

    navigate(`${previousLink}#${activeTab}`, {
      state: {
        licensesModifiedSuccessfullyMessage: 'License updated successfully.'
      }
    });
  };

  if (!location.state) {
    return <Navigate to='/user-management' />;
  }

  if (error || submitError) {
    return <ApolloError />;
  }

  const description =
    'This table manages how many total paid licenses are allocated to organizations and units. ' +
    'The total number of licenses cannot be lower than the number assigned. ' +
    'To reduce the license total below the number assigned, ' +
    'go to User Management and remove assigned licenses from that unit or organization.';

  const columns = getColumns(handleChange, validationErrors, pageMode).filter(item => !item.hidden);

  return (
    <Page title={pageTitle}>
      <Breadcrumb pathItems={breadcrumbPathItems} annotationItems={annotationItems} />

      <div className='page-header'>
        <PageInstructions title='License Management' description={description} />
        <ActionButtonGroup
          isSubmitting={isSubmitting}
          isSubmitDisabled={isSubmitDisabled}
          handleCancelClick={handleCancelClick}
          handleSubmitClick={handleSubmitClick}
        />
      </div>
      <ErrorsContainer validationErrors={validationErrors} />
      <StyledTable
        columns={columns}
        dataSource={tableData}
        pagination={false}
        showSorterTooltip={false}
        rowKey={entry => `${entry.type}${entry.holderId}`}
        scroll={{ y: 650 }}
        loading={loading}
      />
    </Page>
  );
};

const getUpdatedState = (tableData, holderType, holderId, updateValueFunc) =>
  tableData.map(tdr => {
    if (tdr.type === holderType && tdr.holderId === holderId) {
      const rowCopy = { ...tdr };
      updateValueFunc(rowCopy);

      return rowCopy;
    }
    return tdr;
  });

const getChangedObjects = (queried, edited) => _(queried).differenceWith(edited, _.isEqual);

const getColumns = (handleChange, validationErrors, pageMode) => [
  {
    title: 'Type',
    dataIndex: 'typeDisplayName',
    width: '20%'
  },
  {
    title: 'Name',
    dataIndex: 'holderName',
    width: '20%',
    sorter: (a, b) => a.holderName.localeCompare(b.holderName)
  },
  {
    title: 'Paid Assigned',
    dataIndex: 'paidAssigned',
    width: '15%',
    sorter: (a, b) => a.paidAssigned - b.paidAssigned
  },
  {
    title: 'Paid Total',
    dataIndex: 'paidTotal',
    width: '15%',
    sorter: (a, b) => a.paidTotal - b.paidTotal,
    render: (value, record) => (
      <MuiInputNumber
        value={value}
        placeholder={record.paidTotalPlaceholder.toString()}
        error={validationErrors.some(
          ve =>
            ve.holderId === record.holderId &&
            ve.type === record.type &&
            ve.fieldName === 'paidTotal'
        )}
        handleChange={value =>
          handleChange(record.type, record.holderId, record => (record.paidTotal = value))
        }
      />
    )
  },
  {
    title: 'Free Assigned',
    dataIndex: 'freeAssigned',
    width: '15%',
    sorter: (a, b) => a.freeAssigned - b.freeAssigned,
    hidden: pageMode === PAGE_MODE.ISACAVipOrg
  },
  {
    title: 'Free Total',
    dataIndex: 'freeTotal',
    width: '15%',
    sorter: (a, b) => a.freeTotal - b.freeTotal,
    hidden: pageMode === PAGE_MODE.ISACAVipOrg,
    render: (value, record) => (
      <MuiInputNumber
        value={value}
        placeholder={record.freeTotalPlaceholder.toString()}
        error={validationErrors.some(
          ve =>
            ve.holderId === record.holderId &&
            ve.type === record.type &&
            ve.fieldName === 'freeTotal'
        )}
        handleChange={value =>
          handleChange(record.type, record.holderId, record => (record.freeTotal = value))
        }
      />
    )
  }
];

export default LicenseManagementPage;
